import { useEffect, useRef, useState } from "react";
import { FieldWrapper } from "@progress/kendo-react-form";
import { Input } from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";
import clsx from "clsx";

import { Button, ButtonMode } from "../../Button/Button";
import { Icon, IconType } from "../../Icon/Icon";
import { ErrorTooltip } from "../ErrorTooltip";
import { useInputFocus } from "../hooks/useInputFocus";

import type React from "react";
import type { ButtonHandle } from "@progress/kendo-react-buttons";
import type { FieldRenderProps } from "@progress/kendo-react-form";

import "./styles.scss";

interface TextFieldProps extends Partial<FieldRenderProps> {
  name: string;
  modified: boolean;
  maxLength?: number;
  placeholder?: string;
  onClear?: (name: string, event?: React.MouseEvent) => void;
  onChange: FieldRenderProps["onChange"];
  testId?: string;
  wrapperClassName?: string;
  borderClassName?: string;
  inputClassName?: string;
  pattern?: RegExp;
  onPaste?: React.ClipboardEventHandler<HTMLInputElement>;
}

export const TextField = (props: TextFieldProps): JSX.Element => {
  const prevProps = useRef(props);
  const cleanBtnRef = useRef<ButtonHandle>(null);
  const [inputRef, setInputFocus] = useInputFocus();
  const [isFieldFocused, setIsFieldFocused] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(false);
  const hasError =
    props.modified && props.validationMessage?.length ? props.validationMessage : undefined;

  useEffect(() => {
    prevProps.current = props;
  }, [props.modified]); // Only re-run the effect if modified prop changes

  useEffect(() => {
    if (inputRef.current?.element) {
      inputRef.current.element.setAttribute("data-testid", `${props.testId ?? props.name}-field`);
    }
  }, [inputRef, props.testId, props.name]);

  useEffect(() => {
    // update tooltip position on scroll
    let timeoutId: ReturnType<typeof setTimeout>;

    const handleScroll = () => {
      clearTimeout(timeoutId);
      setShowTooltip(false);
      timeoutId = setTimeout(() => {
        if (inputRef?.current?.element?.matches(":focus")) {
          setShowTooltip(true);
        }
      }, 500);
    };

    if (hasError && isFieldFocused) {
      window.addEventListener("scroll", handleScroll, true);
    }

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isFieldFocused, hasError]);

  useEffect(() => {
    if (isMountedRef.current && !isFieldFocused && props.onBlur) {
      props.onBlur();
    }
  }, [isFieldFocused]);

  const handleClear = (event: React.MouseEvent) => {
    event.preventDefault();
    if (props.onClear) {
      props.onClear(props.name, event);
      setInputFocus();
    }
  };

  const onFocus = () => {
    setShowTooltip(true);
    setIsFieldFocused(true);
  };

  const handleChange = (event: { target?: any; value?: any }) => {
    if (event.value && props.pattern && !props.pattern.test(event.value)) {
      return;
    }

    props.onChange(event);
  };

  const onBlur = (event: FocusEvent) => {
    setShowTooltip(false);

    if (props.onClear) {
      const relatedTarget = event.relatedTarget as HTMLElement | null;
      if (cleanBtnRef.current?.element?.contains(relatedTarget)) {
        event.stopPropagation();
      } else {
        setIsFieldFocused(false);
      }
    } else {
      setIsFieldFocused(false);
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const onIconLeave = () => {
    if (!isFieldFocused) {
      setShowTooltip(false);
    }
  };

  return (
    <FieldWrapper
      colSpan={props.colSpan}
      className={clsx("text-field-wrapper", props.wrapperClassName)}
    >
      <div data-testid={props.testId ?? props.name}>
        {props.label && (
          <Label
            className={clsx("text-field-label", props.required && !props.value && "has-asterisks")}
            editorId={props.id}
            editorDisabled={props.disabled}
          >
            {props.label}
          </Label>
        )}
        <ErrorTooltip
          targetElement={inputRef.current?.element}
          error={hasError ? props.validationMessage : null}
          showTooltip={showTooltip}
          isFieldFocused={isFieldFocused}
          setShowTooltip={setShowTooltip}
        >
          <div
            data-testid={`${props.testId ?? props.name}-inner`}
            className={clsx("text-field-border", props.borderClassName, {
              "has-focus": isFieldFocused,
              "text-field-error-border": hasError && isFieldFocused,
            })}
          >
            <Input
              ref={inputRef}
              className={clsx(
                "text-field-input",
                props.inputClassName,
                hasError && "input-error-border",
              )}
              valid={!hasError}
              id={props.id}
              value={props.value}
              onChange={handleChange}
              maxLength={props.maxLength}
              placeholder={props.placeholder}
              onFocus={onFocus}
              onBlur={onBlur}
              title={props.placeholder || props.label || props.name}
              autoComplete="off"
              onKeyDown={props.onKeyDown}
              onPaste={props.onPaste}
            />
            {props.onClear && props.value && (
              <Button
                btnRef={cleanBtnRef}
                type="button"
                iconType={IconType.CancelMark}
                mode={ButtonMode.Clear}
                className="clear-icon"
                onClick={handleClear}
                onFocus={() => {
                  setIsFieldFocused(true);
                }}
                onBlur={onBlur}
              />
            )}
            {hasError && (
              <Icon
                onMouseEnter={() => {
                  setShowTooltip(true);
                }}
                onMouseLeave={onIconLeave}
                className="text-field-alert-icon"
                type={IconType.ErrorExclamation}
              />
            )}
          </div>
        </ErrorTooltip>
      </div>
    </FieldWrapper>
  );
};

export default TextField;
